import { Dialog, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  CSSProperties,
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { atom, useRecoilState } from "recoil";
import { SectionTitle } from "../text/Text.react";
import { twMerge } from "tailwind-merge";
import { uniqueId } from "lodash";
import _ from "lodash";
import { Toaster, ToastPosition } from "react-hot-toast";

const ModalsCountState = atom<string[]>({
  key: "ModalsState",
  default: []
});

const visibleModals = { stack: [] as string[] };

export type ModalProps = {
  open?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  closable?: boolean;
  className?: string;
  positionWrapperClassName?: string;
  style?: CSSProperties;
  positioned?: boolean;
  maxWidthClassName?: string;
  onClickOutside?: () => void;
};

export const Modal = ({ maxWidthClassName, onClickOutside = () => {}, ...props }: ModalProps) => {
  const modalId = useRef(uniqueId());
  const [open, setOpen] = useState(false);
  const [modalsCountState, setModalsCountState] = useRecoilState(ModalsCountState);
  const [level, setLevel] = useState(0);
  const openStatus = useRef<boolean>(false);

  const onClose = useCallback(() => {
    openStatus.current = false;
    visibleModals.stack = visibleModals.stack.filter((a) => a !== modalId.current);
    setModalsCountState(visibleModals.stack);
  }, [setModalsCountState]);

  const onOpen = useCallback(() => {
    openStatus.current = true;
    visibleModals.stack = _.uniq([...visibleModals.stack, modalId.current]);
    setLevel(visibleModals.stack.findIndex((a) => a === modalId.current));
    setModalsCountState(visibleModals.stack);
  }, [setModalsCountState]);

  useEffect(() => {
    if (props.open !== open) {
      setOpen(props.open || false);
      if (props.open) {
        onOpen();
      } else {
        onClose();
      }
    }
  }, [props.open, open, onClose, onOpen]);

  useEffect(
    () => () => {
      if (openStatus.current) onClose();
    },
    [openStatus, onClose]
  );

  const zIndex = "z-[" + (level + 5) + "0]";
  const active = modalsCountState[modalsCountState.length - 1] === modalId.current;
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className={"relative" + zIndex} onClose={onClickOutside}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 pointer-events-none"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0 pointer-events-none"
        >
          <div
            className={"fixed inset-0 bg-black bg-opacity-25 transition-opacity dark:bg-opacity-75"}
          />
        </TransitionChild>

        <div
          className={
            "fixed inset-0 z-10 overflow-y-auto transition-transform" +
            (!active && open
              ? "-translate-y-6 opacity-75 sm:scale-95"
              : !active && !open
                ? "translate-y-6 opacity-75 sm:translate-y-0 sm:scale-100"
                : "")
          }
        >
          <div
            className={twMerge(
              "block flex min-h-dvh items-center justify-center text-center",
              props.positionWrapperClassName
            )}
          >
            {
              /* This element is to trick the browser into centering the modal contents. */
              !props.positioned && (
                <span className="inline-block h-dvh align-middle" aria-hidden="true">
                  &#8203;
                </span>
              )
            }
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 pointer-events-none translate-y-4 sm:translate-y-0"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 pointer-events-none translate-y-4 sm:translate-y-0"
            >
              <Dialog.Panel
                className={twMerge(
                  "relative inline-block w-full w-full transform overflow-hidden rounded-tl-xl rounded-tr-xl bg-white px-4 pt-3 pb-3 text-left align-middle shadow-xl transition-all sm:my-8 sm:w-auto sm:w-full sm:rounded-xl sm:p-6 sm:align-middle sm:align-bottom dark:bg-slate-900",
                  props.className,
                  maxWidthClassName
                )}
                style={props.style || {}}
              >
                {props.closable !== false && (
                  <div className="absolute top-0 right-0 pt-4 pr-4">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none dark:bg-slate-800"
                      onClick={() => props.onClose && props.onClose()}
                      data-cy="button-close-modal"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                )}
                {props.children}
              </Dialog.Panel>
            </TransitionChild>
          </div>
        </div>
        <Toaster
          toastOptions={{
            success: {
              position: "bottom-right" as ToastPosition,
              className: "!z-50"
            },
            error: {
              position: "bottom-right",
              className: "!text-white !bg-red-500 !z-50"
            }
          }}
        />
      </Dialog>
    </Transition>
  );
};

export const ModalContent = (props: {
  title?: string;
  text?: string;
  buttons?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode | ReactNode[];
  theme?: "success" | "danger" | "warning" | "gray";
}) => {
  let color = "indigo";
  if (props.theme === "success") color = "green";
  if (props.theme === "danger") color = "red";
  if (props.theme === "warning") color = "orange";
  if (props.theme === "gray") color = "gray";
  return (
    <>
      <div className="sm:flex sm:items-start">
        {props.icon && (
          <div
            className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-${color}-100 sm:mx-0 sm:h-10 sm:w-10`}
          >
            <div className={`h-6 w-6 text-${color}-600`} aria-hidden="true">
              {props.icon}
            </div>
          </div>
        )}
        <div className={"mt-3 text-center sm:mt-0 sm:text-left" + (props.icon ? "sm:ml-4" : "")}>
          <DialogTitle
            as="h3"
            className="text-center font-medium text-gray-900 text-lg leading-6 dark:text-white"
          >
            <SectionTitle>{props.title}</SectionTitle>
          </DialogTitle>
          <div className="mt-2">
            <p className="text-gray-500 text-sm dark:text-white">{props.text || ""}</p>
          </div>
        </div>
      </div>
      {props.buttons && (
        <div className="mt-5 text-center sm:mt-4 sm:flex sm:flex-row-reverse sm:text-left">
          {props.buttons}
        </div>
      )}
      {props.children}
    </>
  );
};
